import { createContext, useContext } from 'react'

/**
 * A foto antes de subir (RN-PLA-16): o aparelho aceita JPEG, PNG, WebP e HEIC/HEIF, converte HEIC
 * para JPEG quando o navegador sabe decodificar, reduz o lado maior a 1920px e reencoda JPEG com
 * qualidade 0,8; o original acima de 10 MB é recusado antes da compressão. Gera também a miniatura
 * (lado maior 480px, WebP ou JPEG) que vai na confirmação (T41). O desenho é feito aqui, com o
 * canvas do navegador; testes trocam o ambiente por um falso (`PhotoPreparerContext`).
 */

export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif'
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024
export const PHOTO_MAX_SIDE = 1920
export const THUMBNAIL_MAX_SIDE = 480
/** A API ignora miniatura acima de 300 KB: em base64, cerca de 410 mil caracteres. */
export const THUMBNAIL_MAX_DATA_URL_LENGTH = 410_000
export const PHOTO_JPEG_QUALITY = 0.8

export type PhotoMimeType = 'image/jpeg' | 'image/png' | 'image/webp'
export type DetectedFormat = PhotoMimeType | 'image/heic' | null

export const MSG_PHOTO_FORMAT = 'A foto precisa ser JPEG, PNG, WebP ou HEIC. Escolha outro arquivo.'
export const MSG_PHOTO_HEIC_UNSUPPORTED =
  'Este aparelho não consegue converter a foto HEIC. Tire a foto de novo em JPEG (Ajustes › Câmera › Formatos › Mais compatível) ou escolha outra.'
export const MSG_PHOTO_UNREADABLE = 'Não conseguimos ler esta imagem. Escolha outro arquivo.'

/** "A foto tem 14 MB; o máximo é 10 MB. Tire outra ou escolha uma menor." */
export function oversizeMessage(bytes: number) {
  const mb = Math.max(11, Math.ceil(bytes / (1024 * 1024)))
  return `A foto tem ${mb} MB; o máximo é 10 MB. Tire outra ou escolha uma menor.`
}

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'mif1',
  'msf1',
  'heif',
])

/** O formato pelos bytes, não pela extensão nem pelo tipo que o navegador declara. */
export function detectImageFormat(bytes: Uint8Array): DetectedFormat {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg'
  }
  if (
    bytes.length >= 8 &&
    [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((byte, i) => bytes[i] === byte)
  ) {
    return 'image/png'
  }
  if (
    bytes.length >= 12 &&
    String.fromCharCode(...bytes.subarray(0, 4)) === 'RIFF' &&
    String.fromCharCode(...bytes.subarray(8, 12)) === 'WEBP'
  ) {
    return 'image/webp'
  }
  if (bytes.length >= 12 && String.fromCharCode(...bytes.subarray(4, 8)) === 'ftyp') {
    const brand = String.fromCharCode(...bytes.subarray(8, 12)).toLowerCase()
    if (HEIC_BRANDS.has(brand)) return 'image/heic'
  }
  return null
}

/** As medidas reduzidas para caber em `maxSide`, sem esticar o que já cabe. */
export function fitWithin(width: number, height: number, maxSide: number) {
  const longest = Math.max(width, height)
  if (longest <= maxSide) return { width, height }
  const scale = maxSide / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export interface PreparedPhoto {
  blob: Blob
  mimeType: PhotoMimeType
  width: number
  height: number
  /** A miniatura em base64 (WebP ou JPEG, lado maior 480px); nula quando o aparelho não gera. */
  thumbnailDataUrl: string | null
  /** Do EXIF quando houver; sem leitura ainda, a API usa o instante da confirmação. */
  takenAt: string | null
}

export type PrepareResult = { ok: true; photo: PreparedPhoto } | { ok: false; message: string }

export interface DecodedImage {
  width: number
  height: number
  source: CanvasImageSource
  release?: () => void
}

/** O que o navegador faz: decodificar o arquivo e desenhar numa tela do tamanho pedido. */
export interface PhotoEnvironment {
  decode(blob: Blob): Promise<DecodedImage>
  encode(
    image: DecodedImage,
    width: number,
    height: number,
    type: string,
    quality: number,
  ): Promise<Blob | null>
  encodeDataUrl(
    image: DecodedImage,
    width: number,
    height: number,
    type: string,
    quality: number,
  ): string | null
}

function draw(image: DecodedImage, width: number, height: number) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) return null
  context.drawImage(image.source, 0, 0, width, height)
  return canvas
}

export const browserEnvironment: PhotoEnvironment = {
  async decode(blob) {
    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await createImageBitmap(blob)
        return {
          width: bitmap.width,
          height: bitmap.height,
          source: bitmap,
          release: () => bitmap.close(),
        }
      } catch {
        // Alguns navegadores decodificam HEIC só pelo `<img>`; tentamos abaixo.
      }
    }
    const url = URL.createObjectURL(blob)
    try {
      const element = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('undecodable'))
        img.src = url
      })
      return {
        width: element.naturalWidth,
        height: element.naturalHeight,
        source: element,
        release: () => URL.revokeObjectURL(url),
      }
    } catch (error) {
      URL.revokeObjectURL(url)
      throw error
    }
  },
  encode(image, width, height, type, quality) {
    const canvas = draw(image, width, height)
    if (!canvas) return Promise.resolve(null)
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
  },
  encodeDataUrl(image, width, height, type, quality) {
    const canvas = draw(image, width, height)
    if (!canvas) return null
    const url = canvas.toDataURL(type, quality)
    return url.startsWith(`data:${type}`) ? url : null
  },
}

/**
 * Confere e prepara o arquivo: tamanho do original, formato pelos bytes, decodificação (HEIC
 * incluído, quando o aparelho sabe), redução a 1920px, JPEG 0,8 e a miniatura.
 */
export async function preparePhoto(
  file: Blob,
  env: PhotoEnvironment = browserEnvironment,
): Promise<PrepareResult> {
  if (file.size > PHOTO_MAX_BYTES) return { ok: false, message: oversizeMessage(file.size) }
  const head = new Uint8Array(await file.slice(0, 32).arrayBuffer())
  const format = detectImageFormat(head)
  if (!format) return { ok: false, message: MSG_PHOTO_FORMAT }

  let image: DecodedImage
  try {
    image = await env.decode(file)
  } catch {
    return {
      ok: false,
      message: format === 'image/heic' ? MSG_PHOTO_HEIC_UNSUPPORTED : MSG_PHOTO_UNREADABLE,
    }
  }
  try {
    if (!image.width || !image.height) return { ok: false, message: MSG_PHOTO_UNREADABLE }
    const size = fitWithin(image.width, image.height, PHOTO_MAX_SIDE)
    const blob = await env.encode(image, size.width, size.height, 'image/jpeg', PHOTO_JPEG_QUALITY)
    if (!blob) return { ok: false, message: MSG_PHOTO_UNREADABLE }
    const thumb = fitWithin(image.width, image.height, THUMBNAIL_MAX_SIDE)
    const candidate =
      env.encodeDataUrl(image, thumb.width, thumb.height, 'image/webp', PHOTO_JPEG_QUALITY) ??
      env.encodeDataUrl(image, thumb.width, thumb.height, 'image/jpeg', PHOTO_JPEG_QUALITY)
    const thumbnailDataUrl =
      candidate && candidate.length <= THUMBNAIL_MAX_DATA_URL_LENGTH ? candidate : null
    return {
      ok: true,
      photo: {
        blob,
        mimeType: 'image/jpeg',
        width: size.width,
        height: size.height,
        thumbnailDataUrl,
        takenAt: null,
      },
    }
  } finally {
    image.release?.()
  }
}

export type PhotoPreparer = (file: Blob) => Promise<PrepareResult>

/** A preparação ao alcance das telas. Sem provedor, vale a do navegador; teste entrega uma falsa. */
export const PhotoPreparerContext = createContext<PhotoPreparer>((file) => preparePhoto(file))

export function usePhotoPreparer(): PhotoPreparer {
  return useContext(PhotoPreparerContext)
}
